import { ChangeEvent, ComponentProps, Dispatch } from 'react'
import { useTranslation } from 'react-i18next'
import type { Theme } from '@mui/material/styles'
import { makeStyles, useTheme } from '@mui/styles'

import { various } from '@/utils/app/theme'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        alignItems: 'center',
    },

    label: {
        cursor: 'pointer',
        margin: various.margin,
        padding: `10px 30px`,
        textAlign: 'center',
        color: theme.palette.secondary.main,
        backgroundColor: theme.palette.primary.main,
        borderRadius: various.borderRadius,
    },

    input: {
        display: 'none',
    },

    fileList: {
        display: 'flex',
        flexDirection: 'column',
    },
}))

type UploadFileProperties = ComponentProps<'input'> & {
    files: File[]
    setFiles: Dispatch<File[]>
    sizeLimit?: number
}

export const UploadFile = ({
    files,
    setFiles,
    sizeLimit,
    ...properties
}: Readonly<UploadFileProperties>): JSX.Element => {
    const theme = useTheme()
    const classes = useStyles(theme)
    const { t } = useTranslation()

    /** File verification on input file change. */
    const onChange = (event: ChangeEvent<HTMLInputElement>) => {
        const filesToAdd = event.target.files

        if (!sizeLimit) return setFiles([...filesToAdd])

        const filesFiltered = [...filesToAdd].filter(
            (file) => file.size <= sizeLimit,
        )

        setFiles(filesFiltered)
    }

    return (
        <div className={classes.root}>
            {/* Button */}
            <label className={classes.label}>
                {t('chooseAFile')}

                <input
                    className={classes.input}
                    type='file'
                    onChange={onChange}
                    {...properties}
                />
            </label>

            {/* No file chosen */}
            {files.length === 0 && <div>{t('noFileChosen')}</div>}

            {/* File list */}
            <div className={classes.fileList}>
                {files.map((file) => (
                    <div key={file.name}>{file.name}</div>
                ))}
            </div>
        </div>
    )
}
